import { Button, Code, Flex, Tag, TagLabel } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import {
  DateInput,
  FullDateStr,
} from 'packages/common-frontend/components/InputsBETA/DateInput';
import { EditableTitle } from 'packages/common-frontend/components/InputsBETA/EditableTitle';
import { InputCard } from 'packages/common-frontend/components/InputsBETA/InputCard';
import { IsolatedMultiSelectInput } from 'packages/common-frontend/components/InputsBETA/IsolatedMultiSelectInput';
import { SelectInput } from 'packages/common-frontend/components/InputsBETA/SelectInput';
import { TextInput } from 'packages/common-frontend/components/InputsBETA/TextInput';
import { Typography } from 'packages/common-frontend/components/Typography';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PageTemplate } from '~/core/routes/dev/PageTemplate';

type FormData = {
  editableTitle: string;
  basic: string;
  optional: string;
  requiredField: string;
  usd: number | null;
  signedUsd: number | null;
  percentage: number | null;
  number: number | null;
  integer: number | null;
  positiveNumber: number | null;
  positiveInteger: number | null;
  range: { min: number | null; max: number | null };
  containerField1: string;
  select: string;
  select2: string;
  multiSelect: string[];
  date: FullDateStr | null;
  addedInput?: string;
  modifiedInput: string;
  deletedInput?: string;
};

const defaultValues: FormData = {
  editableTitle: 'Fun Form Playground',
  basic: 'Basic text input',
  optional: '',
  requiredField: 'Required value',
  usd: 1234.56,
  signedUsd: -5123.45,
  percentage: 0.25, // 25%
  number: -42.5,
  integer: 100,
  positiveNumber: 3.14,
  positiveInteger: 10,
  containerField1: 'Container input value',
  select: 'one',
  select2: 'two',
  multiSelect: ['value1', 'value3'],
  date: '2022-06-18',
  addedInput: undefined,
  modifiedInput: 'Modified content',
  deletedInput: undefined,
  range: { min: 3434, max: 43 },
};

const blankValues: FormData = {
  editableTitle: '',
  basic: '',
  optional: '',
  requiredField: '',
  usd: null,
  signedUsd: null,
  percentage: null,
  number: null,
  integer: null,
  positiveNumber: null,
  positiveInteger: null,
  containerField1: '',
  select: '',
  select2: '',
  multiSelect: [],
  date: null,
  addedInput: undefined,
  modifiedInput: '',
  deletedInput: undefined,
  range: { min: null, max: null },
};

const MaxRangeInput = ({
  minFieldName,
  maxFieldName,
}: {
  minFieldName: any;
  maxFieldName: any;
}) => {
  const { trigger } = useFormContext();
  const rangeMin = useWatch<FormData>({
    name: minFieldName,
  });

  useEffect(() => {
    trigger([maxFieldName]);
  }, [maxFieldName, rangeMin, trigger]);

  return (
    <TextInput<FormData>
      name={maxFieldName}
      label="Integer Max Range with Validation"
      mask="integer"
      customRules={{
        min: {
          value: Number(rangeMin),
          message: 'Maximum must be greater than or equal to minimum',
        },
      }}
    />
  );
};

const TestTag = (
  <Tag variant="solid-muted" colorScheme="green" size="sm" mb={2}>
    <TagLabel>I&apos;m a Tag!</TagLabel>
  </Tag>
);

const Form = () => {
  const form = useFormContext();

  return (
    <form
      // handleSubmit will only call the callback when the form is valid
      // eslint-disable-next-line no-console
      onSubmit={form.handleSubmit((data) => console.log(data))}
      style={{ width: '45%', margin: '0 2rem' }}
    >
      <Flex gap="1rem" direction="column">
        <EditableTitle<FormData> name="editableTitle" />
        <TextInput<FormData> name="basic" />
        <TextInput<FormData>
          name="optional"
          label="Optional Label"
          helperText="Optional Helper Text"
          placeholder="Optional Placeholder"
        />
        <TextInput<FormData>
          name="requiredField"
          label="Required Field"
          required
        />
        <TextInput<FormData> name="usd" label="USD Mask" mask="usd" />
        <TextInput<FormData>
          name="signedUsd"
          label="Signed USD Mask"
          mask="signedUsd"
        />
        <TextInput<FormData>
          name="percentage"
          label="Percentage Mask"
          mask="percentage"
          tag={TestTag}
        />
        <TextInput<FormData> name="number" label="Number Mask" mask="number" />
        <TextInput<FormData>
          name="integer"
          label="Integer Mask"
          mask="integer"
        />
        <Flex gap="1rem">
          <TextInput<FormData>
            name="range.min"
            label="Integer Min Range with Validation"
            mask="integer"
          />
          <MaxRangeInput minFieldName="range.min" maxFieldName="range.max" />
        </Flex>
        <TextInput<FormData>
          name="positiveNumber"
          label="Positive Number Mask"
          mask="positiveNumber"
        />
        <TextInput<FormData>
          name="positiveInteger"
          label="Positive Integer Mask"
          mask="positiveInteger"
        />

        <DateInput<FormData> name="date" label="Date Field" tag={TestTag} />

        <SelectInput<FormData>
          name="select"
          label="Select Menu"
          options={[
            { label: 'One', value: 'one' },
            { label: 'Two', value: 'two' },
            { label: 'Three', value: 'three' },
          ]}
          tag={TestTag}
        />

        <IsolatedMultiSelectInput<FormData>
          name="multiSelect"
          options={[
            { label: 'Value 1', value: 'value1' },
            { label: 'Value 2', value: 'value2' },
            { label: 'Value 3', value: 'value3' },
          ]}
          label="Isolated Multi Select"
          titleText="Isolated Multi Select Test"
        />

        <InputCard label="Input Container" tag={TestTag}>
          <TextInput<FormData>
            name="containerField1"
            label="Container Field"
            placeholder="Optional Placeholder"
            required
          />
          <SelectInput<FormData>
            name="select2"
            label="Choose Option"
            options={[
              { label: 'One', value: 'one' },
              { label: 'Two', value: 'two' },
              { label: 'Three', value: 'three' },
            ]}
            isDisabled
          />
        </InputCard>
        <Button
          variant="secondary"
          colorScheme="v2-aurora"
          onClick={() => form.reset(blankValues)}
        >
          Reset
        </Button>
        <Button variant="primary" colorScheme="v2-aurora" type="submit">
          Submit
        </Button>
      </Flex>
    </form>
  );
};

const FormValues = () => {
  const formValues = useWatch();

  return (
    <Flex
      width="45%"
      mx="2rem"
      direction="column"
      position="sticky"
      top="2rem"
      height="fit-content"
    >
      <Typography variant="section-subtitle">
        Form Values (stringified):
      </Typography>
      <Code as="pre" whiteSpace="pre-wrap" width="100%" p="1rem">
        {JSON.stringify(formValues, null, 2)}
      </Code>
    </Flex>
  );
};

const ExampleForm = () => {
  const form = useForm<FormData>({ defaultValues, mode: 'onSubmit' }); // This would prevent validation until submit

  return (
    <FormProvider {...form}>
      <Flex width="100%" align="flex-start">
        <Form />
        <FormValues />
      </Flex>
    </FormProvider>
  );
};

export const Route = () => (
  <RouteErrorBoundary
    resetAfterCatch={false}
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <div>Uh oh</div>;
    }}
  >
    <PageTemplate title="./common-frontend/InputsBETA">
      <Flex justify="center" flex="1">
        <ExampleForm />
      </Flex>
    </PageTemplate>
  </RouteErrorBoundary>
);
